import { Link } from "wouter";
import { SEOHead, SOFTWARE_SCHEMA, ORGANIZATION_SCHEMA } from "@/components/seo-head";
import { Button } from "@/components/ui/button";
import { SeoPageHeader } from "@/components/seo-page-header";
import {
  ChevronRight,
  CheckCircle2,
  Smartphone,
  FileText,
  Zap,
  Send,
  Camera,
  Clock,
  ArrowRight,
} from "lucide-react";
import { SeoPageFooter } from "@/components/seo-page-footer";

const PAGE_URL = "https://thepropertool.com/quoting-app-for-builders";

const SOFTWARE_APP_SCHEMA = {
  ...SOFTWARE_SCHEMA,
  "name": "The Proper Tool: Quoting App for Builders",
  "description": "Quoting app for builders. Generate a professional quote from your phone on-site and send a PDF to your client before you leave. Works on any device, no laptop needed.",
  "url": PAGE_URL,
};

const BREADCRUMB_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": [
    { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://thepropertool.com/" },
    { "@type": "ListItem", "position": 2, "name": "Quoting App for Builders", "item": PAGE_URL },
  ],
};

const FAQ_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": [
    {
      "@type": "Question",
      "name": "Is there a quoting app for builders?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Yes. The Proper Tool is a quoting app built for builders. It works in any browser on your phone or tablet, so you can generate and send a professional quote from the client's driveway without going back to your desk. No app store download required.",
      },
    },
    {
      "@type": "Question",
      "name": "Can I use it on my phone on site?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Yes. The Proper Tool is designed to work on any device including mobile phones and tablets. You can describe the job, attach a site photo, generate a fully itemised quote, and send it as a PDF directly from your phone. No laptop or desktop required.",
      },
    },
    {
      "@type": "Question",
      "name": "Does it work without a laptop?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Yes. The Proper Tool runs in any modern browser, so it works on iPhone, Android, iPad, and any tablet or laptop. Many builders use it entirely from their phone. There is no desktop software to install.",
      },
    },
    {
      "@type": "Question",
      "name": "Can I send the quote from the app?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Yes. Once your quote is ready, you can export it as a professionally formatted PDF and share it directly by email from within the app. Clients receive a branded PDF with your company details, full scope, and pricing.",
      },
    },
    {
      "@type": "Question",
      "name": "Is there a free trial of the app?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Yes. The Proper Tool offers a 14-day free trial with one shared allowance of 5 total quote creations across Auto, Template, and Manual quotes and no credit card required. You get full access to all features including mobile quoting, PDF export, and the UK pricing database from day one.",
      },
    },
  ],
};

const FEATURES = [
  {
    icon: Smartphone,
    title: "Works on any device",
    description: "Open it in your phone browser and it works. No app to download, no desktop required. Quote from your van, the client's kitchen, or anywhere with signal.",
  },
  {
    icon: Clock,
    title: "Quote in 10 minutes on site",
    description: "Describe the job in plain language and the AI generates a fully itemised scope with UK pricing. Done before the client's kettle has boiled.",
  },
  {
    icon: Camera,
    title: "Upload a site photo from your phone",
    description: "Take a photo of the job on your phone and attach it directly. The AI uses it alongside your description to build a more accurate quote.",
  },
  {
    icon: Send,
    title: "Send the PDF before you leave",
    description: "Export a branded PDF and share it by email straight from your phone. Clients get a professional document with your company details and full pricing.",
  },
  {
    icon: FileText,
    title: "Full scope, not just a number",
    description: "Every quote comes with a complete line-item breakdown across all trades. No more sending a single total that clients can't understand.",
  },
  {
    icon: Zap,
    title: "UK pricing built in",
    description: "462 UK construction tasks across 31 trade categories, calibrated to current market rates. Low, Typical, and High bands built in from day one.",
  },
];

const HOW_IT_WORKS = [
  {
    step: "1",
    title: "Describe the job on site",
    description: "Type a description of the project from your phone. Attach a site photo, upload architect's drawings if you have them, or just describe what needs doing in plain language.",
  },
  {
    step: "2",
    title: "AI generates the quote",
    description: "The AI produces a fully itemised scope of works: every trade, every line item, with quantities and unit rates drawn from the UK pricing database. Takes under 10 minutes.",
  },
  {
    step: "3",
    title: "Send the PDF to the client",
    description: "Review the quote on your phone, make any adjustments, then export a branded PDF and email it to the client before you've left the site.",
  },
];

const FAQS = [
  {
    q: "Is there a quoting app for builders?",
    a: "Yes. The Proper Tool is a quoting app built for builders. It works in any browser on your phone or tablet, so you can generate and send a professional quote from the client's driveway without going back to your desk. No app store download required.",
  },
  {
    q: "Can I use it on my phone on site?",
    a: "Yes. The Proper Tool is designed to work on any device including mobile phones and tablets. You can describe the job, attach a site photo, generate a fully itemised quote, and send it as a PDF directly from your phone. No laptop or desktop required.",
  },
  {
    q: "Does it work without a laptop?",
    a: "Yes. The Proper Tool runs in any modern browser, so it works on iPhone, Android, iPad, and any tablet or laptop. Many builders use it entirely from their phone. There is no desktop software to install.",
  },
  {
    q: "Can I send the quote from the app?",
    a: "Yes. Once your quote is ready, you can export it as a professionally formatted PDF and share it directly by email from within the app. Clients receive a branded PDF with your company details, full scope, and pricing.",
  },
  {
    q: "Is there a free trial of the app?",
    a: "Yes. The Proper Tool offers a 14-day free trial with one shared allowance of 5 total quote creations across Auto, Template, and Manual quotes and no credit card required. You get full access to all features including mobile quoting, PDF export, and the UK pricing database from day one.",
  },
];

export default function QuotingAppForBuildersPage() {

  return (
    <div className="min-h-screen bg-background text-foreground">
      <SEOHead
        title="Quoting App for Builders: The Proper Tool"
        description="Quoting app for builders. Generate a professional, itemised quote from your phone on site and send the PDF before you leave the client's house. 14-day free trial, no credit card."
        canonical="/quoting-app-for-builders"
        schema={[SOFTWARE_APP_SCHEMA, BREADCRUMB_SCHEMA, FAQ_SCHEMA, ORGANIZATION_SCHEMA]}
      />

      <SeoPageHeader />

      <main>
        {/* Hero */}
        <section className="py-16 sm:py-24 bg-gradient-to-b from-primary/5 to-background">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-6 justify-center">
              <Link href="/" className="hover:text-foreground transition-colors">Home</Link>
              <ChevronRight className="h-4 w-4" />
              <span>Quoting App for Builders</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight tracking-tight mb-6">
              Quoting App for Builders
            </h1>
            <p className="text-lg sm:text-xl text-muted-foreground leading-relaxed mb-8 max-w-3xl mx-auto">
              Quote from your phone on site. Generate a professional, fully itemised <Link href="/glossary#scope-of-works" className="text-primary underline underline-offset-2 hover:text-primary/80">scope of works</Link> PDF in under 10 minutes and send it to the client before you leave. No laptop needed.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button size="lg" asChild>
                <Link href="/login">
                  Start free, no card needed
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
              <Button size="lg" variant="outline" asChild>
                <Link href="/quoting-software-for-builders">See all features</Link>
              </Button>
            </div>
            <p className="text-sm text-muted-foreground mt-4">14-day free trial · Plans from £29/mo · No credit card required</p>
          </div>
        </section>

        {/* How it works */}
        <section className="py-16 sm:py-20 border-t border-border/40" id="how-it-works">
          <div className="max-w-5xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold mb-4">Quote on site in three steps</h2>
              <p className="text-lg text-muted-foreground">From arriving at the job to having a PDF in the client's inbox.</p>
            </div>
            <div className="grid sm:grid-cols-3 gap-8">
              {HOW_IT_WORKS.map((step) => (
                <div key={step.step} className="relative text-center">
                  <div className="w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold text-lg mb-4 mx-auto">
                    {step.step}
                  </div>
                  <h3 className="text-xl font-semibold mb-2">{step.title}</h3>
                  <p className="text-muted-foreground leading-relaxed">{step.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Feature grid */}
        <section className="py-16 sm:py-20 bg-muted/30 border-t border-border/40" id="features">
          <div className="max-w-5xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold mb-4">Built for builders who work on the move</h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">Everything you need to quote quickly and professionally, from any device, anywhere.</p>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {FEATURES.map((f) => (
                <div key={f.title} className="bg-background rounded-xl border border-border p-6">
                  <f.icon className="h-6 w-6 text-primary mb-3" />
                  <h3 className="font-semibold text-base mb-2">{f.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{f.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Stats */}
        <section className="py-16 sm:py-20 border-t border-border/40">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <div className="grid sm:grid-cols-3 gap-8 text-center">
              <div>
                <p className="text-4xl font-bold text-primary mb-2">10 min</p>
                <p className="text-muted-foreground">Average time to generate a full itemised quote from a phone</p>
              </div>
              <div>
                <p className="text-4xl font-bold text-primary mb-2">462</p>
                <p className="text-muted-foreground">UK construction rates across 31 trade categories</p>
              </div>
              <div>
                <p className="text-4xl font-bold text-primary mb-2">£29/mo</p>
                <p className="text-muted-foreground">Starting price, less than one hour of a builder's time</p>
              </div>
            </div>
          </div>
        </section>

        {/* Pricing callout */}
        <section className="py-16 sm:py-20 bg-muted/30 border-t border-border/40" id="pricing">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold mb-4">Simple, transparent pricing</h2>
              <p className="text-lg text-muted-foreground">Start with a 14-day free trial. No credit card required.</p>
            </div>
            <div className="grid sm:grid-cols-3 gap-6">
              {[
                { name: "Lite", price: "£29", desc: "For sole traders", features: ["10 AI auto-quotes/mo", "Unlimited template & manual quotes", "Professional PDF export", "Full 31-category cost library"], highlight: false },
                { name: "Starter", price: "£59", desc: "For growing businesses", features: ["25 AI auto-quotes/mo", "Everything in Lite", "Pricing Intelligence", "Expert validation pipeline", "Regional pricing"], highlight: true },
                { name: "Pro", price: "£99", desc: "For high-volume builders", features: ["50 AI auto-quotes/mo", "Everything in Starter", "Drawing analysis", "Learned rate adjustments"], highlight: false },
              ].map((plan) => (
                <div
                  key={plan.name}
                  className={`rounded-xl border p-6 flex flex-col ${plan.highlight ? "border-primary bg-primary/5" : "border-border bg-background"}`}
                >
                  <div className="mb-4">
                    <p className="font-bold text-lg">{plan.name}</p>
                    <p className="text-3xl font-bold mt-1">{plan.price}<span className="text-base font-normal text-muted-foreground">/mo</span></p>
                    <p className="text-sm text-muted-foreground mt-1">{plan.desc}</p>
                  </div>
                  <ul className="space-y-2 flex-1 mb-6">
                    {plan.features.map((f) => (
                      <li key={f} className="flex items-start gap-2 text-sm">
                        <CheckCircle2 className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                  <Button variant={plan.highlight ? "default" : "outline"} asChild className="w-full">
                    <Link href="/login">Start free trial</Link>
                  </Button>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 sm:py-20 border-t border-border/40">
          <div className="max-w-3xl mx-auto px-4 sm:px-6">
            <h2 className="text-3xl sm:text-4xl font-bold mb-10 text-center">Frequently asked questions</h2>
            <div className="space-y-8">
              {FAQS.map((faq) => (
                <div key={faq.q} className="border-b border-border/60 pb-8 last:border-0 last:pb-0">
                  <h3 className="font-semibold text-lg mb-3">{faq.q}</h3>
                  <p className="text-muted-foreground leading-relaxed">{faq.a}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Internal links */}
        <section className="py-12 border-t border-border/40 bg-muted/20">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <h2 className="text-xl font-semibold mb-6">Related guides</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              {[
                { href: "/blog/builder-quote-app-uk", label: "The Best Builder Quote App for UK Tradespeople" },
                { href: "/quoting-software-for-builders", label: "Quoting Software for Builders: Full Feature Overview" },
                { href: "/tradesman-quoting-tool", label: "Tradesman Quoting Tool" },
                { href: "/job-quoting-software", label: "Job Quoting Software" },
                { href: "/quoting-software-for-contractors", label: "Quoting Software for Contractors" },
                { href: "/free-trial/", label: "Free Trial" },
              ].map((link) => (
                <Link
                  key={link.href}
                  href={link.href}
                  className="flex items-start gap-2 text-sm text-primary hover:underline font-medium"
                >
                  <ChevronRight className="h-4 w-4 flex-shrink-0 mt-0.5" />
                  {link.label}
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 sm:py-20 border-t border-border/40">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">Quote from site today</h2>
            <p className="text-lg text-muted-foreground mb-8">
              Join builders using The Proper Tool to generate professional quotes from their phones and win more jobs.
            </p>
            <Button size="lg" asChild>
              <Link href="/login">
                Start your free 14-day trial
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
            <p className="text-sm text-muted-foreground mt-4">No credit card required · Cancel any time</p>
          </div>
        </section>
      </main>

      <SeoPageFooter />
    </div>
  );
}
